<template>
    <div
        class="bg-white/50 hover:bg-white/70 transition shadow-sm rounded-lg"
        :class="{ 'animate-inbox-blink': props.highlighted }"
    >
        <button
            type="button"
            class="flex w-full items-center gap-2 px-3 py-2.5 text-start bg-transparent border-0 cursor-pointer rounded-lg"
            data-test="category-toggle"
            @click="toggle"
        >
            <span
                class="inline-block size-3 rounded-[3px] shrink-0"
                :style="{ backgroundColor: '#' + props.category.color }"
            ></span>
            <span class="flex-1 truncate text-sm text-slate-600">{{ props.category.name }}</span>
            <vx-icon-loading v-if="props.loadingCounts" class="w-2.5 h-2.5" spin />
            <vx-badge v-else-if="props.count > 0" color="primary" size="2xs">
                {{ props.count }}
            </vx-badge>
            <font-awesome-icon
                widthAuto
                class="text-xs text-slate-400 transition-transform"
                :class="{ 'rotate-180': active }"
                :icon="faChevronDown"
            ></font-awesome-icon>
        </button>
        <div v-if="active" class="px-2 pb-2 flex flex-col gap-1.5">
            <message-hub-thread-sidebar-row
                v-for="thread in threads"
                :key="`${thread.group_id}-${thread.contact_id}`"
                :thread="thread"
            >
                <template v-if="shouldShowSuggestion(thread)" v-slot:afterContent>
                    <motion.div
                        :animate="{
                            height: getSuggestedReply(thread) === undefined ? 34 : getSuggestedReply(thread) ? 34 : 0,
                            opacity: getSuggestedReply(thread) === '' ? 0 : 1,
                        }"
                        :transition="{ duration: 0.3 }"
                        class="overflow-hidden"
                    >
                        <div
                            v-if="getSuggestedReply(thread) === undefined"
                            data-test="suggested-reply-loading"
                            class="rounded-sm bg-slate-300 animate-pulse h-[34px]"
                        ></div>
                        <div
                            v-else-if="getSuggestedReply(thread)"
                            data-test="suggested-reply-preview"
                            class="rounded-sm flex items-center bg-violet-50 border border-violet-100 gap-2 px-2.5 py-2"
                        >
                            <font-awesome-icon :icon="faSparkles" class="text-violet-500 text-sm" />
                            <p class="text-xs text-violet-900 line-clamp-1 m-0">
                                {{ getSuggestedReply(thread) }}
                            </p>
                        </div>
                    </motion.div>
                </template>
            </message-hub-thread-sidebar-row>

            <vx-alert v-if="error" color="danger" :closeable="false">
                {{ error }}

                <template v-slot:right>
                    <vx-button @click="fetchThreads" type="button" size="sm" color="danger">
                        Retry
                        <font-awesome-icon :icon="faRefresh"></font-awesome-icon>
                    </vx-button>
                </template>
            </vx-alert>

            <div v-else-if="loading && threads.length === 0" class="text-center">
                <vx-icon-loading class="w-6 h-6 my-2.5" spin></vx-icon-loading>
            </div>

            <vx-button
                v-if="!isLast && threads.length > 0"
                size="sm"
                color="muted"
                class="w-full mt-1"
                :loading="loading && threads.length > 0"
                @click="fetchThreads"
            >
                Load More
            </vx-button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faChevronDown, faRefresh, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxIconLoading } from '@voxie/frontend-components';
import { inject, onMounted, onUnmounted, ref } from 'vue';
import { motion } from 'motion-v';
import type { Features } from '~/provides/features.types';
import Bus from '~/globals/eventBus';
import MessageHubThreadSidebarRow from '~/components/message-hub/message-hub-threads/MessageHubThreadSidebarRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { threadsClient } from '~/services';
import type { Thread } from '~/services/threads/threadsClient.types';
import type { ThreadCategoryType } from '~/services/threadCategories/threadCategoriesClient.types';
import type { ThreadSectionValue } from '~/constants/messageHub';
import { useIntelligentInbox } from './useIntelligentInbox';
import { isSameThread, shouldShowThreadSuggestion } from '~/components/message-hub/messageHubUtil';

const FEATURES = inject('FEATURES') as Features;
const { fetchSuggestion } = useIntelligentInbox();

const props = defineProps<{
    category: ThreadCategoryType;
    sectionValue: ThreadSectionValue;
    loadingCounts: boolean;
    count: number;
    highlighted: boolean;
}>();

const { getContext } = useCurrentGroup();

const active = defineModel('active', { default: false });
const fetched = ref(false);
const {
    list: threads,
    loading,
    error,
    fetchParams,
    fetchRecords,
    isLast,
} = useCursorPagination<Thread>({
    suppressToast: true,
});

const shouldShowSuggestion = (thread: Thread): boolean => {
    return shouldShowThreadSuggestion(FEATURES, {
        ...thread,
        thread_section: thread.thread_section ?? props.sectionValue,
    });
};

const suggestedReplies = ref<Record<string, string | undefined>>({});

const suggestionKey = (thread: { contact_id: number; group_id?: string }): string => {
    return `${thread.group_id ?? ''}:${thread.contact_id}`;
};

const getSuggestedReply = (thread: { contact_id: number; group_id?: string }): string | undefined => {
    return suggestedReplies.value[suggestionKey(thread)];
};

const setSuggestedReply = (thread: { contact_id: number; group_id?: string }, reply: string): void => {
    suggestedReplies.value = {
        ...suggestedReplies.value,
        [suggestionKey(thread)]: reply,
    };
};

const fetchSuggestions = (targetThreads: Thread[]): void => {
    for (const thread of targetThreads) {
        if (!shouldShowSuggestion(thread)) {
            continue;
        }

        fetchSuggestion(thread.group_id ?? '', thread.contact_id).then((reply) => {
            setSuggestedReply(thread, reply);
        });
    }
};

const fetchThreads = async (): Promise<void> => {
    const newThreads = await fetchRecords(
        threadsClient.getThreads(getContext(), {
            thread_section: props.sectionValue,
            thread_category_id: props.category.id ?? 0,
            cursor: fetchParams.value.cursor ?? undefined,
            limit: 20,
        })
    );

    if (newThreads) {
        fetched.value = true;
        fetchSuggestions(newThreads);
    }
};

const toggle = (): void => {
    active.value = !active.value;
    if (active.value && !fetched.value) {
        fetchThreads();
    }
};

const belongsToMe = (thread: { thread_section?: string; thread_category_id?: number | null }): boolean =>
    thread.thread_section === props.sectionValue && (thread.thread_category_id ?? null) === (props.category.id ?? null);

const handleThreadUpdated = ({ thread }: { thread: Thread }): void => {
    if (!fetched.value) {
        return;
    }

    const existingIndex = threads.value.findIndex((existing) => isSameThread(existing, thread));

    if (existingIndex !== -1) {
        if (belongsToMe(thread)) {
            threads.value[existingIndex] = { ...threads.value[existingIndex], ...thread };

            if (active.value && shouldShowSuggestion(thread) && getSuggestedReply(thread) === undefined) {
                fetchSuggestion(thread.group_id ?? '', thread.contact_id).then((reply) => {
                    setSuggestedReply(thread, reply);
                });
            }
        } else {
            threads.value.splice(existingIndex, 1);
        }
    } else if (belongsToMe(thread)) {
        threads.value.unshift(thread);
        if (active.value && shouldShowSuggestion(thread)) {
            fetchSuggestion(thread.group_id ?? '', thread.contact_id).then((reply) => {
                setSuggestedReply(thread, reply);
            });
        }
    }
};

onMounted(() => {
    Bus.$on('threadUpdated', handleThreadUpdated);
});

onUnmounted(() => {
    Bus.$off('threadUpdated', handleThreadUpdated);
});
</script>
